import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { CATEGORIES } from '../api';

// Two-mode gate: Sign in / Register. Register writes user metadata to
// auth.users (first_name, last_name, company, retail_categories) and a
// row to siteiq_profiles so the backend can join on user_id later.
// Supabase Auth handles the session + token refresh; we just observe
// its state.
export default function LoginGate({ children }) {
  const [session, setSession] = useState(null);
  const [ready, setReady]     = useState(false);
  const [mode, setMode]       = useState('signin');
  const [recovery, setRecovery] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session || null);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === 'PASSWORD_RECOVERY') {
        setRecovery(true);
      }
    });
    return () => sub?.subscription?.unsubscribe?.();
  }, []);

  if (!ready) return null;

  if (recovery && session) {
    return (
      <div style={styles.screen}>
        <div style={styles.card}>
          <div style={styles.title}>
            Site<span style={{ color: '#111' }}>IQ</span>
          </div>
          <ResetPasswordForm onDone={() => setRecovery(false)} />
        </div>
      </div>
    );
  }

  if (session) return children;

  return (
    <div style={styles.screen}>
      <div style={styles.card}>
        <div style={styles.title}>
          Site<span style={{ color: '#111' }}>IQ</span>
        </div>
        <div style={styles.tabs}>
          <button
            type="button"
            onClick={() => setMode('signin')}
            style={{ ...styles.tab, ...(mode === 'signin' ? styles.tabActive : {}) }}
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={() => setMode('signup')}
            style={{ ...styles.tab, ...(mode === 'signup' ? styles.tabActive : {}) }}
          >
            Register
          </button>
        </div>
        {mode === 'signin' ? <SignInForm /> : <RegisterForm onDone={() => setMode('signin')} />}
      </div>
    </div>
  );
}

function SignInForm() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      const msg = (error.message || '').toLowerCase();
      if (msg.includes('not confirmed') || msg.includes('email not confirmed')) {
        setError('Your email hasn\u2019t been confirmed yet — check your inbox for the link.');
      } else {
        setError(error.message || 'Sign-in failed');
      }
    }
  };

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <Field label="Email"    type="email"    value={email}    onChange={setEmail} autoFocus />
      <Field label="Password" type="password" value={password} onChange={setPassword} />
      {error && <div style={styles.error}>{error}</div>}
      <button
        type="submit"
        style={{ ...styles.primary, opacity: loading || !email || !password ? 0.6 : 1 }}
        disabled={loading || !email || !password}
      >
        {loading ? 'Signing in…' : 'Sign in'}
      </button>
      {resetSent ? (
        <div style={styles.resetSent}>Password reset link sent — check your email.</div>
      ) : (
        <button
          type="button"
          onClick={async () => {
            if (!email) { setError('Enter your email first'); return; }
            setError(null);
            const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, {
              redirectTo: window.location.origin,
            });
            if (resetErr) { setError(resetErr.message); } else { setResetSent(true); }
          }}
          style={styles.resetBtn}
        >
          Forgot password?
        </button>
      )}
    </form>
  );
}

function RegisterForm({ onDone }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]         = useState('');
  const [company, setCompany]     = useState('');
  const [accountType, setAccountType] = useState('retailer');
  const [brand, setBrand]         = useState('');
  const [categories, setCategories] = useState([]);
  const [products, setProducts]   = useState([]);
  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [confirmEmail, setConfirmEmail] = useState(null);

  const toggleCategory = (key) => {
    setCategories((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords don\u2019t match'); return; }
    if (categories.length === 0 && !products.includes('sc_feasibility')) { setError('Pick at least one retail category or Shopping Centre Feasibility'); return; }

    setLoading(true);
    const { data, error: signUpErr } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: {
          first_name: firstName,
          last_name:  lastName,
          company,
          brand: accountType === 'retailer' ? brand : '',
          account_type: accountType,
          retail_categories: categories,
          product_interests: products,
        },
      },
    });
    setLoading(false);

    if (signUpErr) {
      setError(signUpErr.message || 'Sign-up failed');
      return;
    }

    // Profile row is created by the on_auth_user_created trigger in
    // Supabase (see backend/README.md). No client-side insert needed.
    if (data?.session) {
      // Email confirmation is off — user is already signed in and
      // onAuthStateChange will flip the gate to render children.
      return;
    }
    // Email confirmation is on. Show the "check your email" state.
    setConfirmEmail(email);
  };

  const toggleProduct = (key) => {
    setProducts((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const canSubmit =
    firstName && lastName && email && company && password && confirm &&
    (categories.length > 0 || products.includes('sc_feasibility'));

  if (confirmEmail) {
    return (
      <div style={styles.confirmMsg}>
        <div style={styles.confirmTitle}>Check your email</div>
        <div style={styles.confirmBody}>
          We sent a confirmation link to <strong>{confirmEmail}</strong>.
          Click it to activate your account, then come back here to sign in.
        </div>
        <button
          type="button"
          onClick={() => { setConfirmEmail(null); onDone?.(); }}
          style={styles.primary}
        >
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <div style={styles.row}>
        <Field label="First name" value={firstName} onChange={setFirstName} autoFocus />
        <Field label="Surname"    value={lastName}  onChange={setLastName} />
      </div>
      <Field label="Email"   type="email" value={email}   onChange={setEmail} />
      <Field label="Company / group" value={company} onChange={setCompany} />

      <div>
        <div style={styles.label}>Account type</div>
        <div style={styles.productGrid}>
          <button type="button" onClick={() => setAccountType('retailer')}
            style={{ ...styles.productChip, ...(accountType === 'retailer' ? styles.catChipActive : {}) }}>
            Retail Brand
          </button>
          <button type="button" onClick={() => setAccountType('property_fund')}
            style={{ ...styles.productChip, ...(accountType === 'property_fund' ? styles.catChipActive : {}) }}>
            Property Fund / Owner
          </button>
        </div>
      </div>

      {accountType === 'retailer' && (
        <Field label="Brand" value={brand} onChange={setBrand} placeholder="e.g. KFC, Shoprite, Dis-Chem" />
      )}

      <div>
        <div style={styles.label}>What are you interested in? <span style={styles.hint}>pick one or more</span></div>
        <div style={styles.productGrid}>
          <button
            type="button"
            onClick={() => toggleProduct('quick_report')}
            style={{ ...styles.productChip, ...(products.includes('quick_report') ? styles.catChipActive : {}) }}
          >
            <span style={{ fontSize: 18, marginRight: 6 }}>&#128205;</span>
            Quick Report · R15,000
          </button>
          <button
            type="button"
            onClick={() => toggleProduct('sc_feasibility')}
            style={{ ...styles.productChip, ...(products.includes('sc_feasibility') ? styles.catChipActive : {}) }}
          >
            <span style={{ fontSize: 18, marginRight: 6 }}>&#127978;</span>
            SC Feasibility · R45,000
          </button>
        </div>
      </div>

      {products.includes('quick_report') && (
        <div>
          <div style={styles.label}>Retail categories <span style={styles.hint}>pick one or more</span></div>
          <div style={styles.catGrid}>
            {CATEGORIES.map((c) => {
              const active = categories.includes(c.key);
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => toggleCategory(c.key)}
                  style={{ ...styles.catChip, ...(active ? styles.catChipActive : {}) }}
                >
                  <span style={{ marginRight: 6 }}>{c.icon}</span>{c.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div style={styles.row}>
        <Field label="Password"         type="password" value={password} onChange={setPassword} />
        <Field label="Confirm password" type="password" value={confirm}  onChange={setConfirm} />
      </div>

      {error && <div style={styles.error}>{error}</div>}
      <button
        type="submit"
        style={{ ...styles.primary, opacity: loading || !canSubmit ? 0.6 : 1 }}
        disabled={loading || !canSubmit}
      >
        {loading ? 'Creating account…' : 'Create account'}
      </button>
    </form>
  );
}

function ResetPasswordForm({ onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [success, setSuccess]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords don’t match'); return; }
    setLoading(true);
    const { error: updateErr } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updateErr) {
      setError(updateErr.message || 'Failed to update password');
    } else {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <div style={styles.confirmMsg}>
        <div style={styles.confirmTitle}>Password Updated</div>
        <div style={styles.confirmBody}>Your password has been changed successfully.</div>
        <button type="button" onClick={onDone} style={styles.primary}>Continue to Dashboard</button>
      </div>
    );
  }

  return (
    <form style={styles.form} onSubmit={handleSubmit}>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '1px' }}>Set New Password</div>
      </div>
      <Field label="New password" type="password" value={password} onChange={setPassword} autoFocus />
      <Field label="Confirm password" type="password" value={confirm} onChange={setConfirm} />
      {error && <div style={styles.error}>{error}</div>}
      <button
        type="submit"
        style={{ ...styles.primary, opacity: loading || !password || !confirm ? 0.6 : 1 }}
        disabled={loading || !password || !confirm}
      >
        {loading ? 'Updating...' : 'Update Password'}
      </button>
    </form>
  );
}

function Field({ label, type = 'text', value, onChange, autoFocus, placeholder }) {
  return (
    <label style={styles.fieldWrap}>
      <span style={styles.label}>{label}</span>
      <input
        type={type}
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={styles.input}
      />
    </label>
  );
}

const styles = {
  screen: {
    width: '100vw', height: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--cream)',
    overflowY: 'auto',
    padding: '24px 16px',
  },
  card: {
    background: 'var(--white)',
    border: '2px solid var(--ink)',
    borderRadius: 10,
    padding: '32px 24px',
    maxWidth: 520,
    width: '100%',
    display: 'flex', flexDirection: 'column', gap: 18,
    boxShadow: '6px 6px 0 0 var(--ink)',
  },
  title: {
    fontFamily: "'News Cycle', serif",
    fontSize: 30, color: 'var(--ink)', textAlign: 'center',
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid var(--rule)',
  },
  tab: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    padding: '10px 0',
    color: 'var(--muted)',
    fontFamily: "'DM Mono', monospace",
    fontSize: 12,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
  },
  tabActive: {
    color: '#111',
    borderBottom: '2px solid #111',
  },
  form: {
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  row: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
  },
  fieldWrap: {
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  label: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 10,
    color: 'var(--muted)',
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
  },
  hint: {
    fontSize: 9,
    color: 'var(--muted)',
    textTransform: 'none',
    letterSpacing: 0,
    marginLeft: 6,
    opacity: 0.6,
  },
  input: {
    padding: '10px 12px',
    borderRadius: 6,
    border: '1px solid var(--rule)',
    background: 'var(--cream)',
    color: 'var(--ink)',
    fontFamily: "'Work Sans', sans-serif",
    fontSize: 14,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  productGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6,
  },
  productChip: {
    padding: '12px 10px',
    borderRadius: 8,
    border: '1px solid var(--rule)',
    background: 'var(--cream)',
    color: 'var(--ink)',
    fontFamily: "'Work Sans', sans-serif",
    fontSize: 12,
    cursor: 'pointer',
    textAlign: 'center',
    fontWeight: 500,
  },
  catGrid: {
    display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6,
  },
  catChip: {
    padding: '6px 10px',
    borderRadius: 16,
    border: '1px solid var(--rule)',
    background: 'var(--cream)',
    color: 'var(--ink)',
    fontFamily: "'Work Sans', sans-serif",
    fontSize: 11,
    cursor: 'pointer',
  },
  catChipActive: {
    border: '1px solid #111',
    background: 'rgba(0,0,0,0.06)',
    color: '#111',
  },
  primary: {
    padding: '10px 14px',
    borderRadius: 6,
    border: 'none',
    background: 'var(--ink)',
    color: 'var(--cream)',
    fontFamily: "'Work Sans', sans-serif",
    fontSize: 14,
    cursor: 'pointer',
    fontWeight: 600,
    marginTop: 4,
  },
  error: {
    fontSize: 12,
    color: 'var(--red)',
    fontFamily: "'DM Mono', monospace",
    textAlign: 'center',
  },
  resetBtn: {
    background: 'transparent',
    border: 'none',
    color: 'var(--muted)',
    fontSize: 12,
    cursor: 'pointer',
    fontFamily: "'Work Sans', sans-serif",
    textAlign: 'center',
    padding: 0,
    width: '100%',
  },
  resetSent: {
    fontSize: 12,
    color: 'var(--green)',
    fontFamily: "'DM Mono', monospace",
    textAlign: 'center',
  },
  confirmMsg: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    alignItems: 'center',
    textAlign: 'center',
    padding: '12px 4px',
  },
  confirmTitle: {
    fontFamily: "'News Cycle', serif",
    fontSize: 20,
    color: '#111',
  },
  confirmBody: {
    fontFamily: "'Work Sans', sans-serif",
    fontSize: 13,
    color: 'var(--muted)',
    lineHeight: 1.6,
    maxWidth: 380,
  },
};
